import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  CheckCheck,
  CircleX,
  Eye,
  Mail,
  Phone,
  Send,
  TriangleAlert,
  User,
  UserMinus,
} from "lucide-react";
import {
  CAMPAIGN_RESULT_PAGE_SIZES,
  type CampaignResultPageSize,
  type CampaignResultRecipientRow,
  type CampaignResultStatus,
  type CampaignResultsRecipientsResponse,
} from "@multi-chat/shared";
import {
  TableWorkspace,
  TableWorkspaceFooter,
  TableWorkspaceScroll,
} from "../../components/common/TableWorkspace.js";
import { Button } from "../../components/ui/button/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import {
  contactsTableBodyCellClass,
  contactsTableBodyRowClass,
  contactsTableClass,
  contactsTableHeadRowClass,
  contactsTableHeaderCellClass,
  contactsTableHeaderIconWrapClass,
  contactsTableHeaderLabelClass,
  contactsTableHeaderStickyTopClass,
  contactsTableShellClass,
  contactsValueChipClass,
} from "../contacts/contacts-table-presentation.js";
import { recipientRangeBounds } from "./campaigns-presentation.js";
import { CampaignProcessingIcon } from "./CampaignProcessingIcon.js";
import { CampaignTablePageSizeSelect } from "./CampaignTablePageSizeSelect.js";
import {
  campaignResultDisplayName,
  campaignResultRowReason,
  campaignResultStatusChipClass,
  campaignResultsHaveActiveFilters,
} from "./campaign-results-presentation.js";

function ResultHeaderCell({
  icon: Icon,
  label,
}: {
  icon: LucideIcon;
  label: string;
}): React.JSX.Element {
  return (
    <th className={cn(contactsTableHeaderCellClass, contactsTableHeaderStickyTopClass)}>
      <span className={contactsTableHeaderLabelClass}>
        <span className={contactsTableHeaderIconWrapClass} aria-hidden="true">
          <Icon size={12} />
        </span>
        <span>{label}</span>
      </span>
    </th>
  );
}

function ResultStatusIcon({ status }: { status: CampaignResultStatus }): React.JSX.Element {
  switch (status) {
    case "PROCESSING":
      return <CampaignProcessingIcon />;
    case "SENT":
      return <Send size={12} aria-hidden="true" />;
    case "DELIVERED":
      return <CheckCheck size={12} aria-hidden="true" />;
    case "READ":
      return <Eye size={12} aria-hidden="true" />;
    case "FAILED":
      return <CircleX size={12} aria-hidden="true" />;
    case "EXCLUDED":
      return <UserMinus size={12} aria-hidden="true" />;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function CampaignResultsTable({
  recipients,
  loading,
  page,
  pageSize,
  search,
  resultStatus,
  onPageChange,
  onPageSizeChange,
  onClearFilters,
}: {
  recipients: CampaignResultsRecipientsResponse | null;
  loading: boolean;
  page: number;
  pageSize: CampaignResultPageSize;
  search: string;
  resultStatus: CampaignResultStatus | "ALL";
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: CampaignResultPageSize) => void;
  onClearFilters: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const items = recipients?.items ?? [];
  const filtersActive = campaignResultsHaveActiveFilters({ search, resultStatus });
  const total = recipients?.total ?? 0;
  const totalPages = Math.max(recipients?.totalPages ?? 1, 1);
  const range = recipientRangeBounds({
    offset: (page - 1) * pageSize,
    count: items.length,
  });

  return (
    <TableWorkspace className={cn(contactsTableShellClass, "min-h-[16rem] flex-1")} data-testid="campaign-results-table">
      <TableWorkspaceScroll>
        <table className={cn(contactsTableClass, "min-w-[48rem]")}>
          <thead>
            <tr className={contactsTableHeadRowClass}>
              <ResultHeaderCell icon={User} label={t("campaigns.results.columns.name")} />
              <ResultHeaderCell icon={Phone} label={t("campaigns.results.columns.phone")} />
              <ResultHeaderCell icon={Mail} label={t("campaigns.results.columns.email")} />
              <ResultHeaderCell icon={Activity} label={t("campaigns.results.columns.result")} />
              <ResultHeaderCell icon={TriangleAlert} label={t("campaigns.results.columns.reason")} />
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td className={contactsTableBodyCellClass} colSpan={5}>
                  <div className="grid gap-2 py-3" aria-busy="true">
                    <div className="h-7 animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none" />
                    <div className="h-7 animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none" />
                    <div className="h-7 animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none" />
                  </div>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td className={contactsTableBodyCellClass} colSpan={5}>
                  <div className="grid justify-items-start gap-2 py-3" data-testid="campaign-results-empty">
                    <p className="m-0 font-medium">
                      {filtersActive
                        ? t("campaigns.results.noMatching")
                        : t("campaigns.results.empty")}
                    </p>
                    {filtersActive ? (
                      <Button type="button" size="sm" variant="outline" onClick={onClearFilters}>
                        {t("campaigns.results.clearFilters")}
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <ResultRow key={row.recipientId} row={row} />
              ))
            )}
          </tbody>
        </table>
      </TableWorkspaceScroll>

      {recipients ? (
        <TableWorkspaceFooter>
          <p className="m-0 text-[0.75rem] text-muted tabular-nums" data-testid="campaign-results-range">
            {t("campaigns.results.showingRange", {
              start: range.start,
              end: range.end,
              total: new Intl.NumberFormat(i18n.language).format(total),
            })}
          </p>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <CampaignTablePageSizeSelect
              value={pageSize}
              sizes={CAMPAIGN_RESULT_PAGE_SIZES}
              ariaLabel={t("campaigns.results.pageSize")}
              testId="campaign-results-page-size"
              onChange={onPageSizeChange}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-[0.75rem]"
              disabled={page <= 1 || loading}
              onClick={() => onPageChange(page - 1)}
              data-testid="campaign-results-prev"
            >
              {t("campaigns.results.prev")}
            </Button>
            <span className="text-[0.75rem] text-muted tabular-nums">
              {t("campaigns.results.pageOf", { page, totalPages })}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-[0.75rem]"
              disabled={page >= totalPages || loading}
              onClick={() => onPageChange(page + 1)}
              data-testid="campaign-results-next"
            >
              {t("campaigns.results.next")}
            </Button>
          </div>
        </TableWorkspaceFooter>
      ) : null}
    </TableWorkspace>
  );
}

function ResultRow({ row }: { row: CampaignResultRecipientRow }): React.JSX.Element {
  const { t } = useTranslation();
  const reason = campaignResultRowReason(t, row);
  const name = campaignResultDisplayName(row.name);

  return (
    <tr className={contactsTableBodyRowClass}>
      <td className={contactsTableBodyCellClass}>
        <span className="font-medium">{name}</span>
      </td>
      <td className={contactsTableBodyCellClass}>
        <LtrValue className={cn(contactsValueChipClass, "max-w-full")} title={row.phone}>
          {row.phone}
        </LtrValue>
      </td>
      <td className={contactsTableBodyCellClass}>
        {row.email ? (
          <LtrValue className={cn(contactsValueChipClass, "max-w-full")} title={row.email}>
            {row.email}
          </LtrValue>
        ) : (
          "—"
        )}
      </td>
      <td className={contactsTableBodyCellClass}>
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.6875rem] font-medium",
            campaignResultStatusChipClass(row.resultStatus),
          )}
          data-testid="campaign-result-status-chip"
        >
          <ResultStatusIcon status={row.resultStatus} />
          {t(`campaigns.results.status.${row.resultStatus}`)}
        </span>
      </td>
      <td className={contactsTableBodyCellClass}>
        {reason ? (
          <span className="grid min-w-0 gap-0.5">
            <span
              className="text-[0.75rem] text-muted"
              title={reason.secondary ?? reason.technicalCode ?? undefined}
            >
              {reason.primary}
            </span>
            {reason.secondary ? (
              <span className="text-[0.7rem] text-subtle">{reason.secondary}</span>
            ) : null}
            {reason.technicalCode ? (
              <details className="min-w-0">
                <summary className="cursor-pointer text-[0.7rem] text-subtle">
                  {t("campaigns.results.technicalDetails")}
                </summary>
                <LtrValue className="mt-0.5 block text-[0.7rem] text-subtle">
                  {reason.technicalCode}
                </LtrValue>
              </details>
            ) : null}
          </span>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}
